import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MaterialSymbol, { toolbarIconButtonSx } from '../components/schedules/MaterialSymbol';
import {
  createSchedule,
  deleteSchedule,
  duplicateSchedule,
  listSchedulesForProject,
  type ScheduleListItem,
  type ProjectPhase,
} from '../services/schedulesService';

const formatDate = (s: string | null) => (s ? s : '—');
const formatMoney = (n: number) =>
  n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Lista harmonogramów (wersji) przypisanych do jednego projektu. */
const ScheduleProjectListPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [project, setProject] = React.useState<{
    project_id: string;
    project_name: string;
    client_name: string | null;
    project_phase: ProjectPhase;
  } | null>(null);
  const [rows, setRows] = React.useState<ScheduleListItem[]>([]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newNotes, setNewNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [menuState, setMenuState] = React.useState<{ anchor: HTMLElement; scheduleId: number } | null>(null);
  const [reloadTick, setReloadTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId) return;
      setLoading(true);
      setLoadError(null);
      try {
        const { project: proj, data } = await listSchedulesForProject(projectId);
        if (cancelled) return;
        setProject(proj);
        setRows(data);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Błąd wczytywania');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, reloadTick]);

  const handleCreate = async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const created = await createSchedule(projectId, {
        name: newName.trim() || undefined,
        notes: newNotes.trim() || undefined,
      });
      setCreateOpen(false);
      setNewName('');
      setNewNotes('');
      navigate(`/app/schedules/project/${encodeURIComponent(projectId)}/schedule/${created.id}`);
    } catch (err) {
      setSnackbar({ open: true, message: 'Nie udało się utworzyć harmonogramu.' });
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (scheduleId: number) => {
    setMenuState(null);
    try {
      await duplicateSchedule(scheduleId);
      setReloadTick((n) => n + 1);
      setSnackbar({ open: true, message: 'Zduplikowano harmonogram.' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Nie udało się zduplikować.' });
    }
  };

  const handleDelete = async (scheduleId: number) => {
    setMenuState(null);
    if (!window.confirm('Usunąć harmonogram wraz ze wszystkimi zadaniami?')) return;
    try {
      await deleteSchedule(scheduleId);
      setReloadTick((n) => n + 1);
    } catch (err) {
      setSnackbar({ open: true, message: 'Nie udało się usunąć harmonogramu.' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return <Box sx={{ p: 3, color: '#c62828', fontFamily: 'Roboto, sans-serif' }}>{loadError}</Box>;
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: '#FAFAFA',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e8eaed',
          bgcolor: '#FFFFFF',
        }}
      >
        <IconButton
          size="small"
          aria-label="Wróć do listy projektów"
          onClick={() => navigate('/app/schedules')}
          sx={{ color: '#020242' }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '18px',
              fontWeight: 500,
              color: '#020242',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {project?.project_name || 'Projekt'} — Harmonogramy
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#5F6368' }}>
            {project?.client_name || ''} · {project?.project_phase || ''}
          </Typography>
        </Box>
        <Tooltip title="Nowy harmonogram (nowa wersja)">
          <IconButton size="small" onClick={() => setCreateOpen(true)} sx={toolbarIconButtonSx}>
            <MaterialSymbol name="add" color="#1A73E8" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: '#5F6368',
              fontFamily: 'Roboto, sans-serif',
              bgcolor: '#FFFFFF',
              border: '1px dashed #dadce0',
              borderRadius: 1,
            }}
          >
            Brak harmonogramów. Utwórz pierwszą wersję przyciskiem „+".
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: '#FFFFFF',
              border: '1px solid #e8eaed',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 2fr 120px 120px 120px 140px 160px 56px',
                px: 1,
                py: 0.75,
                bgcolor: '#F1F3F4',
                borderBottom: '1px solid #dadce0',
                fontSize: 12,
                fontWeight: 600,
                color: '#3c4043',
              }}
            >
              <Box>#</Box>
              <Box>Nazwa / Uwagi</Box>
              <Box sx={{ textAlign: 'right' }}>Zadań</Box>
              <Box sx={{ textAlign: 'right' }}>Suma [zł]</Box>
              <Box>Zakres</Box>
              <Box>Data stanu</Box>
              <Box>Zaktualizowano</Box>
              <Box sx={{ textAlign: 'center' }}>Akcje</Box>
            </Box>
            {rows.map((r, idx) => (
              <Box
                key={r.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '40px 2fr 120px 120px 120px 140px 160px 56px',
                  px: 1,
                  py: 1,
                  borderBottom: '1px solid #f1f3f4',
                  fontSize: 13,
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#EEF3FF' },
                }}
                onClick={() =>
                  navigate(
                    `/app/schedules/project/${encodeURIComponent(projectId || '')}/schedule/${r.id}`
                  )
                }
              >
                <Box sx={{ color: '#5F6368' }}>{idx + 1}</Box>
                <Box>
                  <Box sx={{ fontWeight: 500, color: '#202124' }}>{r.name}</Box>
                  {r.notes && (
                    <Box
                      sx={{
                        fontSize: 12,
                        color: '#5F6368',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.notes}
                    </Box>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>{r.tasks_count}</Box>
                <Box sx={{ textAlign: 'right' }}>{formatMoney(r.tasks_value_sum)}</Box>
                <Box sx={{ fontSize: 12, color: '#5F6368' }}>
                  {r.min_start && r.max_end ? `${r.min_start} → ${r.max_end}` : '—'}
                </Box>
                <Box sx={{ fontSize: 12, color: '#5F6368' }}>{formatDate(r.state_date)}</Box>
                <Box sx={{ fontSize: 12, color: '#5F6368' }}>
                  {new Date(r.updated_at).toLocaleString('pl-PL')}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuState({ anchor: e.currentTarget, scheduleId: r.id });
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={menuState?.anchor || null}
        open={!!menuState}
        onClose={() => setMenuState(null)}
      >
        <MenuItem onClick={() => menuState && handleDuplicate(menuState.scheduleId)}>
          Duplikuj (nowa wersja)
        </MenuItem>
        <MenuItem
          onClick={() => menuState && handleDelete(menuState.scheduleId)}
          sx={{ color: '#B00020' }}
        >
          Usuń harmonogram
        </MenuItem>
      </Menu>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Nowy harmonogram</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 380 }}>
          <TextField
            label="Nazwa (wersja)"
            size="small"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`Harmonogram ${new Date().toISOString().slice(0, 10)}`}
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
          />
          <TextField
            label="Uwagi (opcjonalnie)"
            size="small"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={busy}>
            Anuluj
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={busy}>
            Utwórz
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ScheduleProjectListPage;
